import React, { useState } from 'react';
import {
  Box, Typography, Paper, Divider, Stack, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
} from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { __t } from '../../../i18n/t';
import { Ask } from '../../../components/wizardKit/basics';
import SimulationPanel, { SimulationResult } from './SimulationPanel';
import { AudienceValue } from './AudienceStep';
import { LevelValue, RecoveryDiscount } from './LevelsStep';
import { REWARD_TYPE_LABEL } from './LevelRewardEditor';

interface ReviewStepProps {
  audience: AudienceValue;
  levels: LevelValue[];
  recoveryDiscounts: RecoveryDiscount[];
  sendPush: boolean;
  sendSms: boolean;
  notificationTime: string | null;
  maxTotalSpend: number | null;
  simulation: SimulationResult | null;
  simulationLoading: boolean;
  simulationUnavailableReason?: string | null;
  simulationError?: string | null;
  customRate: number;
  onCustomRateChange: (rate: number) => void;
  onConfirm: () => void;
  submitting: boolean;
}

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const ReviewStep: React.FC<ReviewStepProps> = ({
  audience, levels, recoveryDiscounts, sendPush, sendSms, notificationTime, maxTotalSpend,
  simulation, simulationLoading, simulationUnavailableReason, simulationError, customRate, onCustomRateChange, onConfirm, submitting,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const totalDurationDays = simulation?.totalDurationDays ?? levels.reduce((acc, l) => acc + (l.durationDays || 0), 0);
  const endsAtLabel = simulation ? formatDate(simulation.endsAt) : formatDate(new Date(Date.now() + totalDurationDays * 86400000).toISOString());

  const discountName = (id: string | null) => {
    const d = recoveryDiscounts.find((x) => x.id === id);
    if (!d) return 'Nenhum desconto selecionado';
    return d.description ? `${d.identify} — ${d.description}` : d.identify;
  };

  /** Nível de brinde: o que ele entrega, na mesma coluna em que o de desconto mostra o cupom. */
  const rewardLabel = (level: LevelValue) => {
    const types = [...new Set(level.reward!.rewards.map((r) => REWARD_TYPE_LABEL[r.type]))].join(' + ');
    // mesmo critério do teto: o MAIOR preço de cada recompensa entre os locais
    const cost = level.reward!.rewards.reduce((sum, r) => {
      const values = r.places.map((p) => p.partnerValue ?? 0);
      return sum + (values.length ? Math.max(...values) : 0);
    }, 0);
    return `${types} (R$ ${cost.toFixed(2).replace('.', ',')})`;
  };

  return (
    // Ritmo do cadastro em etapas (o mesmo do desconto v2): 28 px entre blocos.
    <Box sx={{ pt: 2.25, pb: 3.5, display: 'flex', flexDirection: 'column', gap: 3.5, maxWidth: 968 }}>
      <Ask
        id="q-revisao"
        title="Está tudo certo para começar?"
        help={__t('A campanha entra no ar assim que você confirmar.')}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">Duração total</Typography>
            <Typography variant="h6">{totalDurationDays} dias</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Termina em</Typography>
            <Typography variant="h6">{endsAtLabel}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Canais</Typography>
            <Stack direction="row" spacing={1} mt={0.5}>
              {sendPush && <Chip label="Push" size="small" color="primary" />}
              {sendSms && <Chip label="SMS" size="small" color="secondary" />}
              {!sendPush && !sendSms && <Chip label="Nenhum" size="small" />}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Horário</Typography>
            <Typography variant="h6">{notificationTime || 'Assim que possível'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Orçamento</Typography>
            <Typography variant="h6">
              {maxTotalSpend != null ? `R$ ${Number(maxTotalSpend).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'Sem teto'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1}>Níveis</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nível</TableCell>
              <TableCell>O que o nível dá</TableCell>
              <TableCell align="right">Duração</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.map((l) => (
              <TableRow key={l.levelOrder}>
                <TableCell>
                  <Chip label={l.levelOrder} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  {l.reward
                    ? <Chip icon={<CardGiftcardIcon />} size="small" variant="outlined" color="secondary" label={rewardLabel(l)} />
                    : <Chip icon={<LocalGasStationIcon />} size="small" variant="outlined" color={l.discountId ? 'warning' : 'default'} label={discountName(l.discountId)} />}
                </TableCell>
                <TableCell align="right">{l.durationDays} dias</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Divider />

      <SimulationPanel
        simulation={simulation}
        loading={simulationLoading}
        unavailableReason={simulationUnavailableReason}
        error={simulationError}
        customRate={customRate}
        onCustomRateChange={onCustomRateChange}
        hasBudget={maxTotalSpend != null}
      />

      <Box>
        <Button
          variant="contained"
          size="large"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={() => setConfirmOpen(true)}
        >
          Criar e iniciar campanha
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
        <DialogTitle>Criar e iniciar campanha?</DialogTitle>
        <DialogContent>
          <Typography>
            A campanha não pode ser editada depois. Ela começa agora e termina em {endsAtLabel}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={() => { setConfirmOpen(false); onConfirm(); }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewStep;
